import { useEffect, useMemo, useState } from 'react'
import {
    Alert,
    AlertDescription,
    AlertIcon,
    Box,
    Flex,
    Spinner,
    Text
} from '@chakra-ui/react'
import { useSearchParams } from 'react-router-dom'
import { useDuplicateParties } from '@/shared/api/hooks/useDuplicateParties'
import {
    ParentDirectory,
    directoryName,
    useParentDirectory
} from '@/shared/api/hooks/useParentDirectory'
import {
    DUPLICATES_POLL_MS,
    usePendingDuplicates,
    useProcessedDuplicates
} from '@/shared/api/hooks/useParentDuplicates'
import { ParentDuplicateRecord } from '@/shared/api/rest/services/parent-duplicates.service'
import {
    DuplicateStatus,
    duplicateStatus,
    embeddedPartyName,
    getPartyName
} from '../types'
import DuplicateList, { DuplicateFilter } from './DuplicateList'
import DuplicateCompare from './DuplicateCompare'

const DEFAULT_PAGE_SIZE = 10
const SEARCH_DEBOUNCE_MS = 350

const isFilter = (value: string | null): value is DuplicateFilter =>
    value === 'all' || value === 'pending' || value === 'merged' || value === 'dismissed'

/**
 * Whether a pair matches the search box.
 *
 * Both sources are searched: the rows the listing embeds (enriched server-side,
 * so they cover every pair) and the bulk directory (which adds the branch name
 * and still answers for a server that sends the rows bare). Searching only the
 * directory meant a pair past its page cap could not be found by name at all.
 */
const matchesSearch = (
    record: ParentDuplicateRecord,
    directory: ParentDirectory,
    term: string
) => {
    if (!term) return true
    const sides = [directory[record.parentId], directory[record.duplicateId]]
    const embedded = [record.parent, record.duplicate]
    const haystack = [
        ...sides.flatMap((entry) => [
            directoryName(entry),
            entry?.email,
            entry?.phoneNumber,
            entry?.branchName
        ]),
        ...embedded.flatMap((party) => [
            getPartyName(party),
            party?.email,
            party?.phoneNumber
        ]),
        record.parentId,
        record.duplicateId,
        record.duplicateReason
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
    return haystack.includes(term)
}

/**
 * The admin Duplicate Registration screen: the list of suspected pairs and, for
 * whichever row is selected, the side-by-side comparison — both in one page.
 *
 * Rows label themselves from the pair the listing already sent — the server
 * enriches both embedded `parents` rows from `public.users` — and selecting a
 * row fetches that pair's two records in full (`GET /parents/:id`, children
 * included). The bulk parent directory is still loaded alongside: it carries
 * each side's child count and branch name, and it is the fallback that names a
 * row when the listing arrives unenriched.
 */
export default function DuplicateRegistryView() {
    // `?status=` deep-links here (the notification feed sends `status=pending`).
    const [searchParams, setSearchParams] = useSearchParams()
    const statusParam = searchParams.get('status')
    const status: DuplicateFilter = isFilter(statusParam) ? statusParam : 'pending'
    // `?duplicateId=` targets one specific pair, so arriving from a notification
    // opens that comparison rather than whichever row sorts first.
    const requestedId = searchParams.get('duplicateId')

    const pendingQuery = usePendingDuplicates({
        refetchInterval: DUPLICATES_POLL_MS
    })
    // Reviewed pairs are only fetched when they're actually on screen — the
    // processed list grows forever and the default filter never shows it.
    const wantsProcessed = status !== 'pending'
    const processedQuery = useProcessedDuplicates({ enabled: wantsProcessed })

    // One cache entry covering every parent, so row labels and search cost a
    // handful of sequential requests rather than two per row.
    const {
        directory,
        truncated,
        loading: loadingDirectory,
        fetching: fetchingDirectory
    } = useParentDirectory()

    const [searchInput, setSearchInput] = useState('')
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
    // Store only the id (not the record) so the selection can't point at stale
    // data once the underlying list is refetched.
    const [selectedId, setSelectedId] = useState<string | null>(null)

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput.trim().toLowerCase())
            setPage(1)
            setSelectedId(null)
        }, SEARCH_DEBOUNCE_MS)
        return () => clearTimeout(timer)
    }, [searchInput])

    const setStatus = (next: DuplicateFilter) => {
        const params = new URLSearchParams()
        if (next !== 'pending') params.set('status', next)
        setSearchParams(params, { replace: true })
        setPage(1)
        setSelectedId(null)
    }

    // `pending` and `processed` are disjoint by definition, so concatenating
    // them can't produce a duplicate row.
    const all = useMemo(
        () => [...pendingQuery.duplicates, ...processedQuery.duplicates],
        [pendingQuery.duplicates, processedQuery.duplicates]
    )

    const counts = useMemo(() => {
        const tally: Record<DuplicateStatus, number> = {
            pending: 0,
            merged: 0,
            dismissed: 0
        }
        for (const record of all) tally[duplicateStatus(record)] += 1
        return tally
    }, [all])

    const filtered = useMemo(
        () =>
            all
                .filter(
                    (record) => status === 'all' || duplicateStatus(record) === status
                )
                .filter((record) => matchesSearch(record, directory, search)),
        [all, status, directory, search]
    )

    const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize))

    const requestedIndex = requestedId
        ? filtered.findIndex((record) => record._id === requestedId)
        : -1
    const loading = pendingQuery.loading || (wantsProcessed && processedQuery.loading)
    // Requested but absent — already resolved by someone else, or filtered out.
    // Quietly selecting row 0 is how the wrong pair gets merged, so select
    // nobody and say why.
    const requestedMissing = !!requestedId && requestedIndex < 0 && !loading

    // Page is derived, not synced: while `?duplicateId=` is present it resolves
    // to whichever page holds that pair. Any interaction below clears the param,
    // handing control back to `page`.
    const requestedPage =
        requestedIndex >= 0 ? Math.floor(requestedIndex / pageSize) + 1 : null
    const currentPage = Math.min(requestedPage ?? page, pageCount)
    const visible = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize)

    /**
     * Whether any row on this page still has no name to show. Only then is the
     * directory's page cap worth mentioning: with the listing enriched
     * server-side the cap normally costs nothing, and a standing banner about a
     * problem the reader cannot see is just noise.
     */
    const namesMissing = useMemo(
        () =>
            visible.some(
                (record) =>
                    !embeddedPartyName(record) &&
                    !directoryName(directory[record.parentId]) &&
                    !directoryName(directory[record.duplicateId])
            ),
        [visible, directory]
    )

    /** Release the deep link so the admin's own navigation takes over. */
    const clearRequested = () => {
        if (!requestedId) return
        const next = new URLSearchParams(searchParams)
        next.delete('duplicateId')
        setSearchParams(next, { replace: true })
    }

    const selected = requestedId
        ? filtered.find((record) => record._id === requestedId) ?? null
        : filtered.find((record) => record._id === selectedId) ?? null

    // Only the selected pair is fetched in full — two requests, on click.
    const selectedIds = useMemo(
        () => (selected ? [selected.parentId, selected.duplicateId] : []),
        [selected]
    )
    const {
        parties,
        loading: loadingPair,
        error: pairError
    } = useDuplicateParties(selectedIds)

    const error = pendingQuery.error ?? (wantsProcessed ? processedQuery.error : null)

    if (error) {
        return (
            <Box>
                <Text color="red.500">Error: {error.message}</Text>
            </Box>
        )
    }

    if (loading && all.length === 0) {
        return (
            <Flex justify="center" align="center" height="400px">
                <Spinner size="xl" />
            </Flex>
        )
    }

    return (
        <>
            {requestedMissing ? (
                <Alert status="warning" borderRadius="md" mb={3} fontSize="sm">
                    <AlertIcon />
                    <AlertDescription>
                        That pair is no longer in this list — it may already have been
                        resolved. Nothing has been selected.
                    </AlertDescription>
                </Alert>
            ) : null}
            {truncated && namesMissing ? (
                <Alert status="info" borderRadius="md" mb={3} fontSize="sm">
                    <AlertIcon />
                    <AlertDescription>
                        The parent directory is larger than this page can load in full, so
                        a few rows below have no name on them. Selecting a row still loads
                        both records completely.
                    </AlertDescription>
                </Alert>
            ) : null}
            <Flex
                gap={4}
                align="stretch"
                direction={{ base: 'column', xl: 'row' }}
                flex="1"
                minH="0"
            >
                <Box
                    w={{ base: 'full', xl: '340px' }}
                    flexShrink={0}
                    borderWidth="1px"
                    borderColor="gray.100"
                    borderRadius="md"
                    overflow="hidden"
                >
                    <DuplicateList
                        duplicates={visible}
                        directory={directory}
                        directoryLoading={loadingDirectory}
                        total={filtered.length}
                        counts={counts}
                        allCount={all.length}
                        status={status}
                        onStatusChange={setStatus}
                        search={searchInput}
                        onSearch={(value) => {
                            clearRequested()
                            setSearchInput(value)
                        }}
                        selectedId={selected?._id ?? null}
                        onSelect={(id) => {
                            clearRequested()
                            setSelectedId(id)
                        }}
                        page={currentPage}
                        pageCount={pageCount}
                        onPageChange={(nextPage) => {
                            clearRequested()
                            setPage(nextPage)
                            setSelectedId(null)
                        }}
                        pageSize={pageSize}
                        onPageSizeChange={(size) => {
                            clearRequested()
                            setPageSize(size)
                            setPage(1)
                            setSelectedId(null)
                        }}
                        loading={pendingQuery.fetching || fetchingDirectory}
                    />
                </Box>
                <Box
                    flex="1"
                    minW={0}
                    borderWidth="1px"
                    borderColor="gray.100"
                    borderRadius="md"
                    overflowY="auto"
                >
                    <DuplicateCompare
                        record={selected}
                        parties={parties}
                        loading={loadingPair}
                        loadError={pairError}
                        emptyHint="Select a pair on the left to compare the two records side by side."
                    />
                </Box>
            </Flex>
        </>
    )
}
